// What this browser asked the engine to do about an Action center item, until the engine's own read models catch up: an item
// acted on shows "Sent — waiting for the engine" (its button disabled, so a second click cannot send a second act), leaves the
// list the moment its inbox row is `done`, and shows the engine's sentence when the row is refused or failed. The record is
// still the engine's — this only hides an item the engine has already acted on until the engine publishes the engagement at
// or after that act (the snapshot then shows the truth). Nothing here decides a status (rule 9).
import { useSyncExternalStore } from "react";
import type { AttentionItem, InboxRow, InboxStatus } from "../data/types";

/** `auditSeq`: the engagement audit seq of the finished act (from the inbox result), so a snapshot computed after it takes over. */
export interface Settle { inboxId: string; state: InboxStatus; note: string | null; at: number; auditSeq: number | null }
type Store = Map<string, Settle>;

let store: Store = new Map();
const listeners = new Set<() => void>();
const byInbox = new Map<string, string>();
const emit = () => { store = new Map(store); for (const l of listeners) l(); };

export const settleKey = (engagementId: string, itemKey: string) => `${engagementId}:${itemKey}`;

/** An act that settles the item was sent (the inbox row is pending). */
export function markSent(key: string, row: InboxRow) {
  byInbox.set(row.id, key);
  store.set(key, { inboxId: row.id, state: row.status, note: null, at: Date.now(), auditSeq: null });
  emit();
}

/** The engine's words for a finished row: its refusal, or what its result says it did (a skipped act says why). */
export function resultNote(row: Pick<InboxRow, "status" | "refusal" | "result">): string | null {
  if (row.status === "refused" || row.status === "failed") return row.refusal ?? "No reason given.";
  if (row.status !== "done") return null;
  const r = (row.result?.result ?? row.result) as Record<string, unknown> | null | undefined;
  if (!r || typeof r !== "object") return null;
  const sk = r.skipped;
  if (typeof sk === "string") return `Nothing new: ${sk}.`;
  if (Array.isArray(sk) && sk.length) return `Nothing new: ${sk.map((x) => (x && typeof x === "object" ? String((x as Record<string, unknown>).why ?? "") : String(x))).filter(Boolean).join("; ")}.`;
  return null;
}

/** Every inbox row change (EngineProvider feeds them all): update the item it settles, if any. */
export function onInboxRow(row: InboxRow) {
  const key = byInbox.get(row.id);
  if (!key) return;
  const cur = store.get(key);
  if (!cur || cur.inboxId !== row.id) return;
  const audit = (row.result?.audit ?? null) as Record<string, unknown> | null;
  store.set(key, { ...cur, state: row.status, note: resultNote(row), auditSeq: typeof audit?.seq === "number" ? audit.seq : null });
  emit();
}

/** A fresh attention snapshot arrived. A done entry is forgotten once the engine published its engagement at or after the act's
 * audit seq (the snapshot then shows the truth: the item gone, or still there in its new state), or once the item is gone. */
export function reconcile(items: AttentionItem[], engagementIds: string[]) {
  const top = new Map<string, number>();
  for (const i of items) if (typeof i.seq === "number") top.set(i.engagementId, Math.max(top.get(i.engagementId) ?? 0, i.seq));
  const live = new Set(items.map((i) => settleKey(i.engagementId, i.key)));
  let changed = false;
  for (const [k, v] of store) {
    if (v.state !== "done") continue;
    const eng = engagementIds.find((e) => k.startsWith(e + ":"));
    if (!eng) continue;
    const seq = top.get(eng);
    if (!live.has(k) || (v.auditSeq !== null && seq !== undefined && seq >= v.auditSeq)) { store.delete(k); changed = true; }
  }
  if (changed) emit();
}

export function forget(key: string) { if (store.delete(key)) emit(); }
export function _resetSettled() { store = new Map(); byInbox.clear(); emit(); }

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export function useSettled(): Store { return useSyncExternalStore(subscribe, () => store, () => store); }

/** Items still to show: those the engine has done (asked from this browser) leave at once. */
export function visibleItems(items: AttentionItem[], s: Store): AttentionItem[] {
  return items.filter((i) => s.get(settleKey(i.engagementId, i.key))?.state !== "done");
}
